
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  Paper,
  IconButton,
  TableHead,
  CircularProgress,
  Alert,
  Modal,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";

// Import all previews
import ARMHSProgressNotePreview from "./Preview/ARMHSProgressNotePreview";
import ARMHSCrisisPlanPreview from "./Preview/ARMHSCrisisPlanPreview";
import ARMHSDischargeSummaryPreview from "./Preview/ARMHSDischargeSummaryPreview";
import ARMHSITPPreview from "./Preview/ARMHSITPPreview";
import ARMHSFunctionalAssessmentPreview from "./Preview/ARMHSFunctionalAssessmentPreview";
import ARMHSFunctionalAssessmentNewPreview from "./Preview/ARMHSFunctionalAssessmentNewPreview";
import DiagnosticAssessmentPreview from "./Preview/DiagnosticAssessmentPreview";
import AdultDiagnosticAssessmentPreview from "./Preview/AdultDiagnosticAssessmentPreview";
import AODASupervisionNotePreview from "./Preview/AODASupervisionNotePreview";
import GAD7Preview from "./Preview/GAD7Preview";
import CollateralNotePreview from "./Preview/CollateralNotePreview";
import NurseVisitPreview from "./Preview/NurseVisitPreview";
import PostpartumVisitPreview from "./Preview/PostpartumVisitPreview";
import PrenatalVisitPreview from "./Preview/PrenatalVisitPreview";
import IntakeFormPreview from "./Preview/IntakeFormPreview";
import BirthOverviewPreview from "./Preview/BirthOverviewPreview";
import PHQ9Preview from "./Preview/PHQ9Preview";

// Pagination Controls
function TablePaginationActions({ count, page, rowsPerPage, onPageChange }) {
  const theme = useTheme();
  const totalPages = Math.ceil(count / rowsPerPage);
  return (
    <Box sx={{ display: "flex", gap: 1, p: "0 16px" }}>
      <IconButton
        onClick={(e) => onPageChange(e, page - 1)}
        disabled={page === 0}
      >
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={(e) => onPageChange(e, page + 1)}
        disabled={page + 1 >= totalPages}
      >
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
    </Box>
  );
}

const FormsList = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewType, setPreviewType] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const handleClosePreview = useCallback(() => {
    setOpenPreview(false);
    setPreviewData(null);
    setPreviewType(null);
  }, []);

  // Fetch paginated forms
  const fetchForms = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem('neuro_patientportal_auth_token')
      if (!token) throw new Error("Authentication token not found.");

      const res = await fetch(
        `${API_BASE_URL}/PatientPortal/GetPatientForms?PageNumber=${page + 1}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.status === 404) {
        setForms([]);
        setTotalCount(0);
        setError("No forms found for the patient.");
        return;
      }

      if (!res.ok) throw new Error("Failed to fetch forms");

      const data = await res.json();
      if (!Array.isArray(data) || data.length === 0) {
        setForms([]);
        setTotalCount(0);
        setError("No forms found for the patient.");
        return;
      }

      setForms(data);
      setTotalCount(data[0]?.TotalRows || data.length);
    } catch (err) {
      setError(err.message);
      setForms([]);
    } finally {
      setLoading(false);
    }
  }, [page, API_BASE_URL]);

  // Handle view click — NO API CALLS HERE
  const handleView = useCallback((id, title) => {
    const normalized = (title || "").toLowerCase().trim();
    const formMap = {
      "armhs progress note": "ARMHSProgressNote",
      "armhs crisis plan": "ARMHSCrisisPlan",
      "armhs discharge summary": "ARMHSDischargeSummary",
      "armhs itp": "ARMHSITP",
      "armhs functional assessment new": "ARMHSFunctionalAssessmentNew",
      "armhs functional assessment": "ARMHSFunctionalAssessment",
      "adult diagnostic assessment": "AdultDiagnosticAssessment",
      "diagnostic assessment": "DiagnosticAssessment",
      "collateral note": "CollateralNote",
      "aoda supervision": "AODASupervisionNote",
      "aoda collaboration": "AODASupervisionNote",
      "gad7": "GAD7",
      "nurse visit": "NurseVisit",
      "postpartum visit": "PostpartumVisit",
      "prenatal visit": "PrenatalVisit",
      "intake form": "IntakeForm",
      "birth overview": "BirthOverview",
      "phq9": "PHQ9"
    };

    const formType =
      Object.entries(formMap).find(([k]) => normalized.includes(k))?.[1] || "Generic";

    setPreviewData({ patientFormId: id, formTitle: title });
    setPreviewType(formType);
    setOpenPreview(true);
  }, []);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  // ========================== RENDER ==========================
  if (loading)
    return (
      <Box sx={{ height: "60vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress />
      </Box>
    );

  return (
    <>
      <div className="breadcrumb-card">
        <h5>Forms</h5>
        <ul className="breadcrumb">
          <li>
            <Link to="/dashboard">
              <i className="material-symbols-outlined">home</i> Dashboard
            </Link>
          </li>
          <li>Forms</li>
        </ul>
      </div>

      <Card sx={{ p: 3, borderRadius: 2 }}>
        {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

        <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
          <Table>
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Form Title</TableCell>
                <TableCell>Printed</TableCell>
                <TableCell align="center">Supervised Clinician</TableCell>
                <TableCell align="center">Supervisor Sign</TableCell>
                <TableCell align="center">Intern Sign</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {forms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography>No forms available.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                forms.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell>{f.id}</TableCell>
                    <TableCell>{f.Formtitle || f.customFormTitle || "Untitled"}</TableCell>
                    <TableCell>{f.PrintedMark ? "✅" : "❌"}</TableCell>
                    <TableCell align="center">
                      {f.SupervisedClinicianURL?.startsWith("http") ? "✅" : "❌"}
                    </TableCell>
                    <TableCell align="center">
                      {f.SupervisorSignURL?.startsWith("http") ? "✅" : "❌"}
                    </TableCell>
                    <TableCell align="center">
                      {f.internSignURL?.startsWith("http") ? "✅" : "❌"}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        onClick={() => handleView(f.id, f.Formtitle || f.customFormTitle)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>

            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[10]}
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_e, newPage) => setPage(newPage)}
                  onRowsPerPageChange={() => { }}
                  ActionsComponent={TablePaginationActions}
                  colSpan={7}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Card>

      {/* Modal Preview */}
      <Modal open={openPreview} onClose={handleClosePreview}>
        {/* Outer shell: no scroll, keeps the close button above the scroller */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", md: "80%", lg: "70%" },
            bgcolor: "#fff",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden", // contain sticky headers nicely
          }}
        >
          {/* Close button above everything */}
          <IconButton
            onClick={handleClosePreview}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              zIndex: (theme) => theme.zIndex.modal + 1, // keep above sticky headers
              background: "#f0f0f0",
              "&:hover": { background: "#e0e0e0" },
              color: "red",
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Scroll area for preview content (sticky headers stick to this) */}
          <Box
            sx={{
              maxHeight: "90vh",
              overflow: "auto",
              p: 2,
            }}
          >
            {previewType === "CollateralNote" && <CollateralNotePreview data={previewData} />}
            {previewType === "ARMHSProgressNote" && <ARMHSProgressNotePreview data={previewData} />}
            {previewType === "ARMHSCrisisPlan" && <ARMHSCrisisPlanPreview data={previewData} />}
            {previewType === "ARMHSDischargeSummary" && <ARMHSDischargeSummaryPreview data={previewData} />}
            {previewType === "ARMHSITP" && <ARMHSITPPreview data={previewData} />}
            {previewType === "ARMHSFunctionalAssessment" && <ARMHSFunctionalAssessmentPreview data={previewData} />}
            {previewType === "ARMHSFunctionalAssessmentNew" && <ARMHSFunctionalAssessmentNewPreview data={previewData} />}
            {previewType === "DiagnosticAssessment" && <DiagnosticAssessmentPreview data={previewData} />}
            {previewType === "AdultDiagnosticAssessment" && <AdultDiagnosticAssessmentPreview data={previewData} />}
            {previewType === "AODASupervisionNote" && <AODASupervisionNotePreview data={previewData} />}
            {previewType === "GAD7" && <GAD7Preview data={previewData} />}
            {previewType === "NurseVisit" && <NurseVisitPreview data={previewData} />}
            {previewType === "PostpartumVisit" && <PostpartumVisitPreview data={previewData} />}
            {previewType === "PrenatalVisit" && <PrenatalVisitPreview data={previewData} />}
            {previewType === "IntakeForm" && <IntakeFormPreview data={previewData} />}
            {previewType === "BirthOverview" && <BirthOverviewPreview data={previewData} />}
            {previewType === "PHQ9" && <PHQ9Preview data={previewData} />}

            {previewType === "Generic" && (
              <Box sx={{ textAlign: "center", p: 3 }}>
                <Typography variant="h6" color="textSecondary">
                  Preview not available
                </Typography>
                <Typography variant="body2">
                  {previewData?.formTitle || "Unknown form"}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default FormsList;